/**
 * Lightweight SQL parser that extracts table and column references
 * from a SQL query and validates them against a known schema.
 */

export interface SchemaWarning {
  type: "table" | "column";
  name: string;
  /** The table context if it's a column warning */
  table?: string;
  message: string;
}

/** SQL keywords and functions that should NOT be treated as identifiers */
const SQL_NOISE = new Set([
  // Keywords
  "select", "from", "where", "and", "or", "not", "in", "is", "null", "as",
  "on", "join", "left", "right", "inner", "outer", "cross", "full", "natural",
  "group", "by", "order", "asc", "desc", "having", "limit", "offset",
  "insert", "into", "values", "update", "set", "delete", "create", "alter",
  "drop", "table", "index", "view", "case", "when", "then", "else", "end",
  "distinct", "all", "union", "except", "intersect", "between", "like",
  "exists", "any", "some", "true", "false", "interval", "day", "month",
  "year", "hour", "minute", "second", "now", "current_date", "current_timestamp",
  "if", "ifnull", "coalesce", "nullif", "with", "recursive", "over", "partition",
  "rows", "range", "unbounded", "preceding", "following", "current", "row",
  // Aggregates & functions
  "count", "sum", "avg", "min", "max", "concat", "format", "cast",
  "char", "varchar", "int", "integer", "bigint", "decimal", "numeric",
  "float", "double", "date", "datetime", "timestamp", "text", "boolean",
  "signed", "unsigned", "date_format", "str_to_date", "substring", "trim",
  "upper", "lower", "length", "replace", "round", "ceil", "floor", "abs",
  "row_number", "rank", "dense_rank", "lag", "lead", "first_value", "last_value",
  // Common extras
  "ilike", "similar", "to", "escape", "using", "lateral", "unnest",
  "array", "jsonb", "json", "extract", "epoch", "zone", "at", "time",
  "type", "primary", "key", "references", "foreign", "constraint", "default",
  "check", "unique", "cascade", "restrict", "no", "action", "deferrable",
  "initially", "deferred", "immediate", "only", "returning", "conflict",
  "do", "nothing", "excluded", "fetch", "next", "first", "last", "percent",
  "nulls", "filter", "within", "cube", "rollup", "grouping", "sets",
]);

/** Strip SQL comments and string literals to avoid false positives */
function stripNoise(sql: string): string {
  return sql
    // Remove single-line comments
    .replace(/--.*$/gm, "")
    // Remove multi-line comments
    .replace(/\/\*[\s\S]*?\*\//g, "")
    // Remove string literals (single and double quotes)
    .replace(/'[^']*'/g, "'_STR_'")
    .replace(/"[^"]*"/g, '"_STR_"')
    // Remove placeholder syntax {{...}}
    .replace(/\{\{[^}]*\}\}/g, "'_PLACEHOLDER_'");
}

/** Extract identifiers after FROM/JOIN (table names) */
function extractTableRefs(cleanSql: string): string[] {
  const tables: string[] = [];
  // Match FROM table or JOIN table patterns
  const tablePattern = /\b(?:FROM|JOIN)\s+(`?)([\w]+)\1/gi;
  let match: RegExpExecArray | null;
  while ((match = tablePattern.exec(cleanSql)) !== null) {
    const name = match[2];
    if (!SQL_NOISE.has(name.toLowerCase())) {
      tables.push(name);
    }
  }
  return [...new Set(tables)];
}

/** Extract aliases defined via AS keyword or positional aliases in SELECT */
function extractAliases(cleanSql: string): Set<string> {
  const aliases = new Set<string>();

  // Match explicit: ... AS alias
  const asPattern = /\bAS\s+`?([\w]+)`?/gi;
  let match: RegExpExecArray | null;
  while ((match = asPattern.exec(cleanSql)) !== null) {
    aliases.add(match[1].toLowerCase());
  }

  return aliases;
}

/** Extract column references: qualified (table.col) and unqualified identifiers */
function extractColumnRefs(
  cleanSql: string,
  knownTables: Set<string>,
): { qualified: { table: string; column: string }[]; unqualified: string[] } {
  const qualified: { table: string; column: string }[] = [];
  const unqualified: string[] = [];

  // Match qualified: table.column or `table`.`column`
  const qualifiedPattern = /`?([\w]+)`?\s*\.\s*`?([\w]+)`?/g;
  let match: RegExpExecArray | null;
  while ((match = qualifiedPattern.exec(cleanSql)) !== null) {
    const table = match[1];
    const col = match[2];
    if (!SQL_NOISE.has(table.toLowerCase()) && !SQL_NOISE.has(col.toLowerCase())) {
      qualified.push({ table, column: col });
    }
  }

  // For unqualified columns: extract identifiers in SELECT, WHERE, GROUP BY, ORDER BY, HAVING, ON, SET contexts
  // We look for identifiers that are not table names, not SQL keywords, and not after FROM/JOIN
  const identPattern = /\b`?([\w]+)`?\b/g;
  const allIdents: string[] = [];
  while ((match = identPattern.exec(cleanSql)) !== null) {
    allIdents.push(match[1]);
  }

  // Filter: only consider identifiers that aren't SQL keywords, aren't known tables,
  // aren't part of qualified refs, and aren't numeric
  const qualifiedCols = new Set(qualified.map((q) => q.column));
  const tableSet = new Set([...knownTables, ...extractTableRefs(cleanSql).map((t) => t.toLowerCase())]);
  const aliases = extractAliases(cleanSql);

  for (const ident of allIdents) {
    const lower = ident.toLowerCase();
    if (SQL_NOISE.has(lower)) continue;
    if (tableSet.has(lower)) continue;
    if (aliases.has(lower)) continue;
    if (/^\d+$/.test(ident)) continue;
    if (ident.startsWith("_STR_") || ident.startsWith("_PLACEHOLDER_")) continue;
    if (!qualifiedCols.has(ident)) {
      unqualified.push(ident);
    }
  }

  return { qualified, unqualified: [...new Set(unqualified)] };
}

/**
 * Validate SQL against a schema. Returns warnings for unknown tables and columns.
 */
export function validateSqlAgainstSchema(
  sql: string,
  schemaTables: string[],
  schemaColumns: Record<string, string[]>,
): SchemaWarning[] {
  if (!sql.trim() || schemaTables.length === 0) return [];

  const warnings: SchemaWarning[] = [];
  const cleanSql = stripNoise(sql);

  const tableNamesLower = new Map<string, string>();
  for (const t of schemaTables) {
    tableNamesLower.set(t.toLowerCase(), t);
  }

  const columnNamesLower = new Map<string, Set<string>>();
  for (const [table, cols] of Object.entries(schemaColumns)) {
    const colSet = new Set(cols.map((c) => c.toLowerCase()));
    columnNamesLower.set(table.toLowerCase(), colSet);
  }

  // Check tables
  const referencedTables = extractTableRefs(cleanSql);
  const validTableRefs: string[] = [];
  for (const table of referencedTables) {
    const lower = table.toLowerCase();
    if (!tableNamesLower.has(lower)) {
      warnings.push({
        type: "table",
        name: table,
        message: `Table "${table}" not found in schema`,
      });
    } else {
      validTableRefs.push(lower);
    }
  }

  // Check qualified columns (table.column)
  const { qualified, unqualified } = extractColumnRefs(cleanSql, new Set(schemaTables.map((t) => t.toLowerCase())));
  const checkedQualified = new Set<string>();
  for (const { table, column } of qualified) {
    const key = `${table.toLowerCase()}.${column.toLowerCase()}`;
    if (checkedQualified.has(key)) continue;
    checkedQualified.add(key);

    const tableLower = table.toLowerCase();
    if (!tableNamesLower.has(tableLower)) continue; // table warning already shown
    const cols = columnNamesLower.get(tableLower);
    if (cols && !cols.has(column.toLowerCase())) {
      warnings.push({
        type: "column",
        name: column,
        table,
        message: `Column "${column}" not found in table "${tableNamesLower.get(tableLower)}"`,
      });
    }
  }

  // Check unqualified columns against referenced tables
  // A column is valid if it exists in ANY of the referenced tables
  if (validTableRefs.length > 0 && unqualified.length > 0) {
    // Build a combined set of all columns across referenced tables
    const allReferencedColumns = new Set<string>();
    for (const tLower of validTableRefs) {
      const cols = columnNamesLower.get(tLower);
      if (cols) {
        for (const c of cols) allReferencedColumns.add(c);
      }
    }

    for (const col of unqualified) {
      const colLower = col.toLowerCase();
      if (!allReferencedColumns.has(colLower)) {
        // Build a helpful message showing which tables were checked
        const tableList = validTableRefs
          .map((t) => tableNamesLower.get(t) || t)
          .join(", ");
        warnings.push({
          type: "column",
          name: col,
          message: `Column "${col}" not found in ${validTableRefs.length === 1 ? `table "${tableList}"` : `tables: ${tableList}`}`,
        });
      }
    }
  }

  return warnings;
}
